import { useEffect, useState } from "react";

import { ApiError, http, qs } from "../api";
import { Badge, Banner, Empty, Table, useAsync, type Column } from "../ui";
import { BarChartCard, LineChartCard, type BarDatum, type ChartSeries } from "../charts";
import {
  Card,
  ErrorBanner,
  Loadable,
  MetricCard,
  dash,
  errMessage,
  fmtDuration,
  fmtInt,
  fmtNum3,
  fmtPct,
  fmtTime,
  num,
  rateColor,
  shortId,
} from "./format";
import type {
  CasesResponse,
  EvalRun,
  JudgeAccuracy,
  RunDelta,
  TokenSummary,
  TrendResponse,
} from "./types";

// ============================================================ trend tab

/**
 * Cross-run questions: the run list itself, the pass/detection trend, whether
 * the judge is an instrument worth reading, what this run broke and fixed
 * against a baseline, what it cost, and the single-message case results.
 */
export function TrendTab({
  runs,
  runsLoading,
  runsError,
  runId,
  onSelectRun,
}: {
  runs: EvalRun[];
  runsLoading: boolean;
  runsError: ApiError | null;
  runId: string;
  onSelectRun: (id: string) => void;
}) {
  const trend = useAsync<TrendResponse>(
    () => http.get<TrendResponse>(`/chatbot/eval-ops/trend${qs({ limit: 30 })}`),
    []
  );
  const accuracy = useAsync<JudgeAccuracy>(
    () => http.get<JudgeAccuracy>(`/chatbot/eval-ops/judge-accuracy${qs({ run_id: runId })}`),
    [runId]
  );
  const tokens = useAsync<TokenSummary>(
    () =>
      runId ? http.get<TokenSummary>(`/chatbot/eval-ops/tokens${qs({ run_id: runId })}`) : Promise.resolve({}),
    [runId]
  );
  const cases = useAsync<CasesResponse>(
    () =>
      runId
        ? http.get<CasesResponse>(`/chatbot/eval-ops/runs/${encodeURIComponent(runId)}/cases`)
        : Promise.resolve({ run_id: "", cases: [] }),
    [runId]
  );

  // Delta is on demand: it is a comparison the operator chooses, and firing it
  // on every run change would run a two-run diff nobody asked for.
  const [vsRunId, setVsRunId] = useState("");
  const [delta, setDelta] = useState<RunDelta | null>(null);
  const [deltaError, setDeltaError] = useState<ApiError | null>(null);
  const [deltaLoading, setDeltaLoading] = useState(false);

  useEffect(() => {
    setVsRunId("");
    setDelta(null);
    setDeltaError(null);
  }, [runId]);

  const compare = async () => {
    if (!runId || !vsRunId) return;
    setDeltaLoading(true);
    setDeltaError(null);
    try {
      setDelta(await http.get<RunDelta>(`/chatbot/eval-ops/delta${qs({ run_id: runId, vs_run_id: vsRunId })}`));
    } catch (err) {
      setDelta(null);
      setDeltaError(err instanceof ApiError ? err : new ApiError(0, "client_error", errMessage(err, "compare failed"), ""));
    } finally {
      setDeltaLoading(false);
    }
  };

  const points = trend.data?.trend ?? [];
  const rateSeries: ChartSeries[] = [
    { name: "Turn pass rate", points: points.map((p) => ({ x: shortId(p.run_id, 10), y: p.pass_rate ?? null })) },
    { name: "Fraud F1", points: points.map((p) => ({ x: shortId(p.run_id, 10), y: p.fraud_f1 ?? null })) },
    { name: "Fraud recall", points: points.map((p) => ({ x: shortId(p.run_id, 10), y: p.fraud_recall ?? null })) },
    { name: "Spam F1", points: points.map((p) => ({ x: shortId(p.run_id, 10), y: p.spam_f1 ?? null })) },
  ].filter((s) => s.points.some((p) => p.y !== null));

  const sizeBars: BarDatum[] = points.slice(-12).map((p) => ({ label: shortId(p.run_id, 10), value: num(p.n) }));

  const runColumns: Column<EvalRun>[] = [
    {
      key: "run_id",
      header: "Run",
      render: (run) => (
        <span className="truncate" title={run.run_id}>
          {run.run_id === runId ? <span className="strong">{shortId(run.run_id, 22)}</span> : shortId(run.run_id, 22)}
        </span>
      ),
    },
    { key: "suite", header: "Suite", render: (run) => dash(run.suite) },
    { key: "status", header: "Status", render: (run) => <Badge>{dash(run.status)}</Badge> },
    {
      key: "pass_rate",
      header: "Case pass",
      align: "right",
      sortable: true,
      render: (run) => (
        <span style={{ color: rateColor(run.pass_rate, num(run.total)) }}>{fmtPct(run.pass_rate)}</span>
      ),
    },
    { key: "total", header: "Cases", align: "right", sortable: true, render: (run) => fmtInt(run.total) },
    { key: "total_turns", header: "Turns", align: "right", sortable: true, render: (run) => fmtInt(run.total_turns) },
    {
      key: "p0_pass_rate",
      header: "P0 pass",
      align: "right",
      render: (run) => (run.p0_total ? fmtPct(run.p0_pass_rate) : <span className="muted">—</span>),
    },
    { key: "gen_model", header: "Gen model", render: (run) => dash(run.gen_model) },
    { key: "duration_s", header: "Duration", align: "right", render: (run) => fmtDuration(run.duration_s) },
    { key: "recorded_at", header: "Recorded", render: (run) => fmtTime(run.recorded_at ?? run.started_at) },
  ];

  const acc = accuracy.data;
  const tok = tokens.data;
  const tokTurns = tok?.turns ?? {};

  return (
    <div className="stack">
      <Card
        title="Runs"
        note="Every recorded harness run, newest first. Cross-org by design: a harness run grades the product, not a tenant's data. Click a row to make it the selected run."
      >
        <ErrorBanner error={runsError} what="Failed to load eval runs" />
        <Table
          columns={runColumns}
          rows={runs}
          loading={runsLoading}
          empty={<Empty title="No eval runs recorded" body="Nothing has posted a harness run to this store yet." />}
          onRowClick={(run) => onSelectRun(run.run_id)}
        />
      </Card>

      <Card
        title="Trend across runs"
        note="Oldest to newest. Every rate is null — never 0 — when its denominator is 0, so a gap in a line is a run that could not measure that scope, not a run that scored zero."
      >
        <Loadable loading={trend.loading} error={trend.error} what="Failed to load the trend" cols={4}>
          {!points.length ? (
            <Empty title="No trend yet" body="The trend needs at least one run with single-message results." />
          ) : (
            <div className="stack">
              <LineChartCard
                title="Pass rate and detection quality"
                series={rateSeries}
                yLabel="rate"
                note="All four measures are rates in [0,1], so they share one axis."
              />
              <BarChartCard title="Cases scored per run" data={sizeBars} note="Last 12 runs." yLabel="cases" />
            </div>
          )}
        </Loadable>
      </Card>

      <Card
        title="Is the judge an instrument?"
        note="The LLM judge graded against the humans who reviewed it. Everything else on this screen measures the SYSTEM; this measures the instrument. A handful of labels cannot establish reliability, and the card says so."
      >
        <Loadable loading={accuracy.loading} error={accuracy.error} what="Failed to load judge accuracy" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Judge vs human"
              value={fmtPct(acc?.agreement)}
              sub={`${fmtInt(acc?.n_compared)} of ${fmtInt(acc?.n_labeled)} labelled turns compared`}
              sub2={acc?.reliable ? "sample clears the minimum" : `below the ${fmtInt(acc?.min_n)}-label minimum`}
              tone={acc?.reliable ? "default" : "warn"}
              note={
                num(acc?.n_compared) === 0
                  ? "nothing to compare — no turn in this scope carries both a judge verdict and a human one"
                  : null
              }
            />
            <MetricCard
              label="Cohen's kappa"
              value={fmtNum3(acc?.kappa)}
              sub="agreement above chance"
              note={acc?.kappa === null || acc?.kappa === undefined ? "undefined — one marginal is degenerate" : null}
            />
            <MetricCard
              label="False accept"
              value={fmtInt(acc?.false_accept)}
              sub="judge said correct, human said wrong"
              sub2="the judge rubber-stamped a real defect"
              tone={num(acc?.false_accept) > 0 ? "bad" : "default"}
            />
            <MetricCard
              label="False reject"
              value={fmtInt(acc?.false_reject)}
              sub="judge said wrong, human said correct"
              sub2={`${fmtInt(acc?.judge_skips)} judge skips excluded`}
              tone={num(acc?.false_reject) > 0 ? "warn" : "default"}
            />
          </div>
        </Loadable>
      </Card>

      <Card
        title="What changed vs a baseline run"
        note="92% → 92% is equally consistent with nothing changing and with eight cases breaking while eight others were fixed. Only the per-case set difference tells them apart."
        actions={
          <div className="row">
            <select
              value={vsRunId}
              onChange={(e) => setVsRunId(e.target.value)}
              style={{ width: 300 }}
              aria-label="Baseline run"
            >
              <option value="">Compare against…</option>
              {runs
                .filter((r) => r.run_id !== runId)
                .map((r) => (
                  <option key={r.run_id} value={r.run_id}>
                    {r.run_id} ({fmtInt(r.total_turns)} turns)
                  </option>
                ))}
            </select>
            <button className="btn" onClick={() => void compare()} disabled={!vsRunId || deltaLoading}>
              {deltaLoading && <span className="spinner" />}
              Compare
            </button>
          </div>
        }
      >
        <ErrorBanner error={deltaError} what="Failed to compare the runs" />
        {!delta ? (
          <Empty title="Pick a baseline run" body="See what this run broke and what it fixed, case by case." />
        ) : (
          <div className="stack">
            <div className="row" style={{ flexWrap: "wrap", gap: "var(--sp-4)", fontSize: 13 }}>
              <span style={{ color: "var(--critical)" }}>
                Regressed <span className="strong num">{fmtInt(delta.regressed)}</span>
              </span>
              <span style={{ color: "var(--success)" }}>
                Fixed <span className="strong num">{fmtInt(delta.fixed)}</span>
              </span>
              <span className="muted">
                Unchanged <span className="num">{fmtInt(delta.unchanged)}</span>
              </span>
              <span className="muted">
                Added <span className="num">{fmtInt((delta.added ?? []).length)}</span>
              </span>
              <span className="muted">
                Removed <span className="num">{fmtInt((delta.removed ?? []).length)}</span>
              </span>
            </div>
            <div className="grid c2">
              <div>
                <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  Pass → fail
                </h4>
                {!(delta.pass_to_fail ?? []).length ? (
                  <Banner tone="good">Nothing regressed.</Banner>
                ) : (
                  <div className="col">
                    {(delta.pass_to_fail ?? []).slice(0, 30).map((row) => (
                      <div key={row.case_id} style={{ fontSize: 12 }}>
                        <span className="strong">{row.case_id}</span>{" "}
                        <span className="muted">{(row.fails ?? []).join(", ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  Fail → pass
                </h4>
                {!(delta.fail_to_pass ?? []).length ? (
                  <Empty title="Nothing fixed" />
                ) : (
                  <div className="col">
                    {(delta.fail_to_pass ?? []).slice(0, 30).map((row) => (
                      <div key={row.case_id} style={{ fontSize: 12 }}>
                        <span className="strong">{row.case_id}</span>{" "}
                        <span className="muted">was: {(row.was_fails ?? []).join(", ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="What the run cost"
        note="Generation vs judge vs escalation, run-declared beside turn-measured. A run whose harness never reported usage is unmeasured, not free."
      >
        <Loadable loading={tokens.loading} error={tokens.error} what="Failed to load the token summary" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Tokens / turn"
              value={tok?.tokens_per_turn === null || tok?.tokens_per_turn === undefined ? "—" : Number(tok.tokens_per_turn).toFixed(2)}
              sub={`${fmtInt(tok?.measured_total_tokens)} measured over ${fmtInt(tokTurns.turns_measured)} turns`}
              sub2={`${fmtInt(tokTurns.turns_unmeasured)} turns recorded no usage`}
              note={tokTurns.tokens_instrumented ? null : tok?.tokens_per_turn_note ?? "no turn recorded token usage"}
            />
            <MetricCard
              label="Declared by the run"
              value={fmtInt(tok?.run?.declared_total_tokens)}
              sub={`gen ${fmtInt(tok?.run?.generation_tokens)} · judge ${fmtInt(tok?.run?.judge_tokens)}`}
              sub2={`escalation ${fmtInt(tok?.run?.escalation_tokens)}`}
              hint="What the harness said it spent, beside what the per-turn rows actually recorded."
            />
            <MetricCard
              label="LLM calls"
              value={tokTurns.llm_calls === null || tokTurns.llm_calls === undefined ? "—" : fmtInt(tokTurns.llm_calls)}
              sub="summed over the per-turn rows"
              note={
                tokTurns.llm_calls === null || tokTurns.llm_calls === undefined
                  ? "nobody counted the calls — a 0 here would read as a fully graph-served run"
                  : null
              }
            />
            <MetricCard
              label="Zero-LLM turn share"
              value={fmtPct(tokTurns.zero_llm_turn_share, 0)}
              sub={`${fmtInt(tokTurns.zero_llm_turns)} turns recorded no tokens`}
              sub2={tokTurns.zero_llm_turn_share_note ?? undefined}
              note={
                tokTurns.zero_llm_turn_share === null || tokTurns.zero_llm_turn_share === undefined
                  ? tokTurns.zero_llm_turn_share_note ?? "no turn recorded usage, so the share is unknown"
                  : null
              }
            />
          </div>
        </Loadable>
      </Card>

      <Card
        title="Case results (eval_results)"
        note="The single-message detection cases of this run, joined to the latest human label. This is the corpus the per-strata recall above is computed over."
      >
        <Loadable loading={cases.loading} error={cases.error} what="Failed to load the run's cases" cols={7}>
          {!(cases.data?.cases ?? []).length ? (
            <Empty
              title="No case results"
              body="This run posted no single-message detection results — it is a conversation run."
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Stratum</th>
                    <th>Lang</th>
                    <th>Expected</th>
                    <th>KG label</th>
                    <th>Band</th>
                    <th>Judge</th>
                    <th>Human</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(cases.data?.cases ?? []).slice(0, 400).map((row, i) => (
                    <tr key={`${row.case_id ?? "case"}-${i}`}>
                      <td className="truncate" style={{ maxWidth: 200 }} title={row.case_id ?? ""}>
                        {dash(row.case_id)}
                      </td>
                      <td className="truncate" style={{ maxWidth: 180 }}>
                        {dash(row.strata_key)}
                      </td>
                      <td>{dash(row.language)}</td>
                      <td>{dash(row.expected_label)}</td>
                      <td>{dash(row.kg_label)}</td>
                      <td>{dash(row.kg_band)}</td>
                      <td className="truncate" style={{ maxWidth: 160 }} title={row.judge_reason ?? ""}>
                        {dash(row.judge_verdict)}
                      </td>
                      <td>{dash(row.human_verdict)}</td>
                      <td>
                        <Badge tone={row.passed ? "good" : "critical"}>
                          {row.passed ? "pass" : dash(row.failure_mode) || "fail"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(cases.data?.cases ?? []).length > 400 && (
                <p className="muted" style={{ fontSize: 11 }}>
                  {fmtInt((cases.data?.cases ?? []).length)} scored · 400 shown — list capped in the browser.
                </p>
              )}
            </div>
          )}
        </Loadable>
      </Card>
    </div>
  );
}
